// src/components/GenericList.tsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  List,
  ListItemText,
  CircularProgress,
  IconButton,
  Typography,
  Button,
  Box,
  Tooltip,
  Alert,
  Paper,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  Checkbox,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import _ from "lodash";
import { useAuth } from "../../contexts/AuthContext";
import { useUsage } from "../../contexts/UsageContext";
import DocumentChatModal from "../modals/DocumentChatModal";
import DeleteConfirmationModal from "../modals/DeleteConfirmationModal";
import RenameFileModal from "../modals/RenameFileModal";
import BulkDeleteConfirmationModal from "../modals/BulkDeleteConfirmationModal";
import { deleteOutputFile } from "../../actions/files/deleteOutputFile";
import { deleteOutputFilesInBatches } from "../../actions/files/deleteOutputFiles";
import { renameOutputFile } from "../../actions/files/renameOutputFile";
import { downloadOutput } from "../../actions/files/downloadOutput";
import { formatDistanceToNow } from 'date-fns';

export interface ListItemData {
  key: string;
  filename: string;
  title?: string;
  downloadUrl: string;
  viewUrl?: string;
  lastModified?: string | Date;
  updatedAt?: string;
  createdAt?: string;
  status?: string;
  size?: number;
  fileType?: 'PDF' | 'DOCX' | 'TXT' | string;
  // Document chat support (premium feature)
  jobId?: string;                // Job ID for document chat
  chatEnabled?: boolean;         // Whether embeddings are ready
  embeddingsExpiresAt?: string;  // When embeddings expire
}

interface PaginationResult {
  items: ListItemData[];
  lastKey: string | null;
}

interface GenericListProps {
  fetchData: (
    lastKey?: string
  ) => Promise<{ items: ListItemData[]; lastKey: string | null }>;
  refresh: boolean;
  onRefreshComplete: () => void;
  /** Shows a checkbox per row, select all and bulk delete. Off by default. */
  selectable?: boolean;
}

const isSelectableItem = (item: ListItemData): boolean =>
  item.status?.toUpperCase() !== 'PROCESSING';

const GenericList: React.FC<GenericListProps> = ({
  fetchData,
  refresh,
  onRefreshComplete,
  selectable = false,
}) => {
  const { documentChat } = useUsage();
  const hasDocumentChatAccess = documentChat !== null && documentChat !== undefined;
  const [items, setItems] = useState<ListItemData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastKeyVal, setLastKeyVal] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const [selectedFileForRename, setSelectedFileForRename] = useState<ListItemData | null>(null);
  const [renameLoading, setRenameLoading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<ListItemData | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Bulk selection state (only used when `selectable` is set)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [bulkDeleteKeys, setBulkDeleteKeys] = useState<string[] | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false); // collecting all pages, or deleting
  const [bulkMessage, setBulkMessage] = useState<{ severity: 'error' | 'warning'; text: string } | null>(null);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);

  const [downloadLoading, setDownloadLoading] = useState<string | null>(null); // Track which file is downloading

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentItemForMenu, setCurrentItemForMenu] = useState<ListItemData | null>(null);

  // Document chat modal state
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [selectedItemForChat, setSelectedItemForChat] = useState<ListItemData | null>(null);

  const handleOpenChatModal = (item: ListItemData) => {
    setSelectedItemForChat(item);
    setChatModalOpen(true);
  };

  const handleCloseChatModal = () => {
    setChatModalOpen(false);
    setSelectedItemForChat(null);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, item: ListItemData) => {
    setAnchorEl(event.currentTarget);
    setCurrentItemForMenu(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentItemForMenu(null);
  };

  // Bumped by every reload. A response that arrives after a newer reload started is stale and is dropped,
  // so a slow "Load More" can never append its rows onto a freshly reloaded first page.
  const requestGeneration = useRef(0);

  const loadItems = useCallback(
    async (initialLoad = false) => {
      if (loading && !initialLoad) return;
      const generation = initialLoad ? ++requestGeneration.current : requestGeneration.current;
      setLoading(true);
      setError(null);
      if (initialLoad) {
        setSelectedKeys(new Set());
        setBulkMessage(null);
      }
      const keyToFetch = initialLoad ? undefined : lastKeyVal ?? undefined;

      try {
        const result = await fetchData(keyToFetch);
        if (generation !== requestGeneration.current) return;
        setItems((prevItems) =>
          initialLoad ? result.items : [...prevItems, ...result.items]
        );
        setLastKeyVal(result.lastKey);
        setHasMore(result.lastKey !== null && result.items.length > 0);
      } catch (err: any) {
        if (generation !== requestGeneration.current) return;
        setError(err.message || "Failed to load items");
        console.error("Load items error:", err);
      } finally {
        // A stale request leaves loading to the reload that replaced it.
        if (generation === requestGeneration.current) setLoading(false);
      }
    },
    [fetchData, lastKeyVal, loading]
  );

  useEffect(() => {
    loadItems(true);
  }, [fetchData]);

  // loadItems and onRefreshComplete change identity while a reload is running (loading flips, the parent
  // re-renders), which re-runs the effect below. This flag keeps one refresh request to one reload.
  const refreshStarted = useRef(false);

  useEffect(() => {
    if (!refresh) {
      refreshStarted.current = false;
      return;
    }
    // A refresh waits for a running bulk action, so it cannot overwrite what that action is working on.
    if (bulkBusy || refreshStarted.current) return;
    refreshStarted.current = true;
    loadItems(true).finally(() => {
      onRefreshComplete();
    });
  }, [refresh, loadItems, onRefreshComplete, bulkBusy]);

  const handleLoadMore = () => {
    if (hasMore && !loading && !bulkBusy) {
      loadItems();
    }
  };

  const handleDeleteClick = (item: ListItemData) => {
    handleMenuClose();
    setDeleteTarget(item);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setError(null);
    try {
      await deleteOutputFile(deleteTarget.key);
      setDeleteTarget(null);
      await loadItems(true);
    } catch (err: any) {
      console.error("Delete error:", err);
      setError(err.message || "Failed to delete file.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
  };

  const handleRenameClick = (item: ListItemData) => {
    handleMenuClose();
    setSelectedFileForRename(item);
  };

  const confirmRename = async (newDisplayName: string) => {
    if (!selectedFileForRename) return;
    setRenameLoading(true);
    setError(null);

    try {
      // Update display name only (S3 key stays the same)
      await renameOutputFile(selectedFileForRename.key, newDisplayName);
      setSelectedFileForRename(null); 
      await loadItems(true); 
    } catch (err: any) {
      setError(err.message || "Failed to update display name.");
    } finally {
      setRenameLoading(false);
    }
  };

  const cancelRename = () => {
    setSelectedFileForRename(null);
  };

  const handleDownload = async (item: ListItemData) => {
    if (downloadLoading === item.key) return; // Prevent double-clicks
    
    setDownloadLoading(item.key);
    setError(null);
    
    try {
      // Determine the best filename to request from the backend
      let requestedFilename = undefined;
      if (item.title) {
        const extension = item.filename.split('.').pop() || 'docx';
        requestedFilename = item.title.endsWith(`.${extension}`) ? item.title : `${item.title}.${extension}`;
      }

      await downloadOutput(item.key, { requestedFilename });
    } catch (err: any) {
      console.error('Download error:', err);
      setError(err.message || 'Failed to download file.');
    } finally {
      setDownloadLoading(null);
    }
  };

  const selectableKeys = items.filter(isSelectableItem).map((item) => item.key);
  // Only rows that are currently loaded count, so a stale key can never be counted or sent.
  const loadedSelectedKeys = selectableKeys.filter((key) => selectedKeys.has(key));
  const selectedCount = loadedSelectedKeys.length;
  const allSelected = selectableKeys.length > 0 && selectedCount === selectableKeys.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const toggleItemSelected = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedKeys(allSelected ? new Set() : new Set(selectableKeys));
  };

  const handleDeleteSelectedClick = () => {
    setBulkMessage(null);
    setBulkDeleteKeys(loadedSelectedKeys);
  };

  // Delete all: page through the rest of the list first, so every transcript is counted.
  // The dialog is driven by bulkDeleteKeys alone, so cancelling leaves the user's selection as it was.
  const handleDeleteAllClick = async () => {
    setBulkMenuAnchor(null);
    if (loading || bulkBusy) return;
    setBulkMessage(null);
    setBulkBusy(true);
    try {
      let allItems = items;
      let cursor = hasMore ? lastKeyVal : null;
      while (cursor !== null) {
        const page = await fetchData(cursor);
        const known = new Set(allItems.map((item) => item.key));
        allItems = [...allItems, ...page.items.filter((item) => !known.has(item.key))];
        cursor = page.items.length > 0 && page.lastKey !== cursor ? page.lastKey : null;
      }
      const loadedKeys = new Set(items.map((item) => item.key));
      const extraItems = allItems.filter((item) => !loadedKeys.has(item.key));
      // Merge into whatever is loaded now rather than replacing it with the snapshot.
      setItems((prev) => {
        const prevKeys = new Set(prev.map((item) => item.key));
        return [...prev, ...extraItems.filter((item) => !prevKeys.has(item.key))];
      });
      setLastKeyVal(null);
      setHasMore(false);
      const keys = allItems.filter(isSelectableItem).map((item) => item.key);
      if (keys.length === 0) {
        setBulkMessage({
          severity: 'warning',
          text: "There are no transcriptions that can be deleted right now. Transcriptions that are still processing can't be deleted.",
        });
      } else {
        setBulkDeleteKeys(keys);
      }
    } catch (err: any) {
      console.error("Load all items error:", err);
      setBulkMessage({ severity: 'error', text: "Couldn't load all transcriptions. Please try again." });
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmBulkDelete = async () => {
    if (bulkBusy || !bulkDeleteKeys || bulkDeleteKeys.length === 0) return;
    setBulkBusy(true);
    setBulkMessage(null);
    try {
      const { deleted, failed } = await deleteOutputFilesInBatches(bulkDeleteKeys);
      const deletedSet = new Set(deleted);
      const remaining = items.filter((item) => !deletedSet.has(item.key));
      setItems((prev) => prev.filter((item) => !deletedSet.has(item.key)));
      // Every loaded row is gone but more pages exist: reload the first page so they stay reachable.
      // loadItems(true) clears the selection and message, so both are set after it.
      if (remaining.length === 0 && hasMore) {
        await loadItems(true);
      }
      // Failed items stay visible and selected; a clean run clears the selection.
      const remainingKeys = new Set(remaining.map((item) => item.key));
      const stillLoadedFailures = failed.filter((entry) => remainingKeys.has(entry.key));
      setSelectedKeys(new Set(stillLoadedFailures.map((entry) => entry.key)));
      if (failed.length > 0) {
        setBulkMessage({ severity: 'error', text: `${failed.length} couldn't be deleted. Please try again.` });
      }
    } finally {
      setBulkDeleteKeys(null);
      setBulkBusy(false);
    }
  };

  const cancelBulkDelete = () => {
    setBulkDeleteKeys(null);
  };

  const formatFileSize = (bytes?: number): string => {
    if (bytes === undefined || bytes === null || bytes === 0) return '0 KB';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getRelativeDate = (dateValue?: string | Date): string => {
    if (!dateValue) return '';
    try {
      const date = new Date(dateValue);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (e) {
      return 'Invalid date';
    }
  };

  if (loading && items.length === 0) {
    return <CircularProgress id="generic-list-loading" sx={{ color: 'hsl(var(--sidebar-primary))' }} />;
  }

  if (error) {
    return (
      <Box style={{ paddingTop: 0 }} id="generic-list-error-container">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!loading && items.length === 0 && !error) {
    return <Typography sx={{textAlign: 'center', mt: 3, color: 'text.secondary'}}>No items to display.</Typography>;
  }

  return (
    <Box id="generic-list-container" sx={{ width: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {bulkMessage && (
        <Alert severity={bulkMessage.severity} sx={{ mb: 2 }} onClose={() => setBulkMessage(null)}>
          {bulkMessage.text}
        </Alert>
      )}
      {selectable && (
        <Box
          id="generic-list-bulk-header"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 1.5 }}
        >
          <Checkbox
            id="generic-list-select-all"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={toggleSelectAll}
            disabled={bulkBusy || selectableKeys.length === 0}
            inputProps={{ 'aria-label': 'Select all' }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            {`${selectedCount} selected`}
          </Typography>
          <Button
            id="generic-list-delete-selected"
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteSelectedClick}
            disabled={bulkBusy || loading || selectedCount < 1}
          >
            Delete selected
          </Button>
          <IconButton
            id="generic-list-bulk-more"
            aria-label="bulk actions"
            onClick={(event) => setBulkMenuAnchor(event.currentTarget)}
            disabled={bulkBusy || loading}
          >
            {bulkBusy ? <CircularProgress size={20} sx={{ color: 'hsl(var(--sidebar-primary))' }} /> : <MoreVertIcon />}
          </IconButton>
          <Menu
            anchorEl={bulkMenuAnchor}
            open={Boolean(bulkMenuAnchor)}
            onClose={() => setBulkMenuAnchor(null)}
          >
            <MenuItem onClick={handleDeleteAllClick} sx={{ color: 'error.main' }}>
              <DeleteIcon sx={{ mr: 1, fontSize: '1.2rem' }} /> Delete all
            </MenuItem>
          </Menu>
        </Box>
      )}
      <List id="generic-list" disablePadding>
        {items.map((item, index) => {
          const displayFileName = item.title || item.filename || "Unnamed File";
          const itemSize = formatFileSize(item.size);
          const dateToDisplay = item.lastModified || item.updatedAt || item.createdAt;
          const relativeDate = getRelativeDate(dateToDisplay);
          const fileTypeDisplay = item.fileType || item.filename.split('.').pop()?.toUpperCase() || 'FILE';
          
          const isProcessing = !!(item.status && item.status.toUpperCase() === 'PROCESSING');
          const isCompleted = !!(item.status && item.status.toUpperCase() === 'COMPLETED');

          return (
            <Paper 
              key={item.key} 
              elevation={0}
              sx={{ 
                mb: 1.5, 
                p: 1.5, 
                display: 'flex', 
                alignItems: 'center', 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }
              }}
              id={`generic-list-item-${index}`}
            >
              {selectable && (
                <Checkbox
                  id={`generic-list-select-${index}`}
                  checked={selectedKeys.has(item.key)}
                  onChange={() => toggleItemSelected(item.key)}
                  disabled={isProcessing || bulkBusy}
                  inputProps={{ 'aria-label': `Select ${displayFileName}` }}
                  sx={{ mr: 0.5 }}
                />
              )}
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '6px',
                  bgcolor: '#223343',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  mr: 2,
                }}
              >
                <DescriptionIcon sx={{ color: 'white', fontSize: 16 }} />
              </Box>
              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Tooltip title={item.title ? item.filename : ""} placement="top-start">
                  <Typography variant="subtitle1" component="div" noWrap sx={{fontWeight: 500}}>
                    {displayFileName}
                  </Typography>
                </Tooltip>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {`${fileTypeDisplay} • ${itemSize} • ${relativeDate}`}
                </Typography>
              </Box>

              {item.status && (
                <Chip 
                  label={_.capitalize(item.status)} 
                  size="small" 
                  color={isCompleted ? 'success' : isProcessing ? 'info' : item.status.toUpperCase() === 'FAILED' ? 'error' : 'default'}
                  sx={{ mx: 1.5, minWidth: '80px', textAlign: 'center' }} 
                />
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                {/* Document Chat Button - shown for items with chat enabled and premium users */}
                {item.chatEnabled && item.jobId && hasDocumentChatAccess && (
                  <Tooltip title="Chat with Documents" placement="top">
                    <IconButton
                      id={`generic-list-chat-${index}`}
                      onClick={() => handleOpenChatModal(item)}
                      edge="end"
                      aria-label="chat with documents"
                      sx={{ 
                        mr: 0.5,
                        color: 'hsl(var(--primary))',
                        '&:hover': {
                          bgcolor: 'hsl(var(--primary) / 0.1)',
                        }
                      }}
                      disabled={isProcessing}
                    >
                      <ChatBubbleOutlineIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {/* Show disabled chat icon for non-premium users when chat is available */}
                {item.chatEnabled && item.jobId && !hasDocumentChatAccess && (
                  <Tooltip title="Document Chat (Premium Feature)" placement="top">
                    <span>
                      <IconButton
                        disabled
                        edge="end"
                        aria-label="chat premium"
                        sx={{ 
                          mr: 0.5,
                          opacity: 0.4,
                        }}
                      >
                        <ChatBubbleOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {item.viewUrl && (
                  <Tooltip title="View File" placement="top">
                    <IconButton 
                      id={`generic-list-view-${index}`}
                      href={item.viewUrl} 
                      target="_blank"
                      rel="noopener noreferrer"
                      edge="end" 
                      aria-label="view" 
                      sx={{ 
                        mr: 0.5,
                        '&:hover svg': {
                          color: '#4a90e2'
                        }
                      }}
                      disabled={isProcessing}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {(isCompleted || item.status === undefined) && (
                  <Tooltip title="Download File" placement="top">
                    <IconButton
                      id={`generic-list-download-${index}`}
                      onClick={() => handleDownload(item)}
                      edge="end"
                      aria-label="download"
                      sx={{ 
                        mr: 0.5,
                        '&:hover svg': {
                          color: '#4a90e2'
                        }
                      }}
                      disabled={isProcessing || downloadLoading === item.key}
                    >
                      {downloadLoading === item.key ? (
                        <CircularProgress size={20} sx={{ color: 'hsl(var(--sidebar-primary))' }} />
                      ) : (
                        <DownloadIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                
                <Tooltip title="More options" placement="top">
                  <IconButton
                    id={`generic-list-more-${index}`}
                    aria-label="more options"
                    onClick={(event) => handleMenuClick(event, item)}
                    edge="end"
                    disabled={isProcessing || bulkBusy}
                    sx={{
                      '&:hover svg': {
                        color: '#4a90e2'
                      }
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          );
        })}
      </List>

      {currentItemForMenu && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 2,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.2))',
              mt: 0.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem 
            onClick={() => { handleRenameClick(currentItemForMenu!); }} 
            disabled={renameLoading || (currentItemForMenu?.status?.toUpperCase() === 'PROCESSING')}
            sx={{
              '&:hover svg': {
                color: '#4a90e2'
              }
            }}
          >
            <EditIcon sx={{ mr: 1, fontSize: '1.2rem' }} /> Rename
          </MenuItem>
          <MenuItem 
            onClick={() => { handleDeleteClick(currentItemForMenu!); }} 
            disabled={deleteLoading || (currentItemForMenu?.status?.toUpperCase() === 'PROCESSING')} 
            sx={{
              color: 'error.main',
              '&:hover svg': {
                color: '#4a90e2'
              }
            }}
          >
            <DeleteIcon sx={{ mr: 1, fontSize: '1.2rem' }}/> Delete
          </MenuItem>
        </Menu>
      )}

      {hasMore && (
        <Box sx={{ textAlign: "center", mt: 2, mb: 2 }}>
          <Button onClick={handleLoadMore} disabled={loading || bulkBusy} variant="outlined">
            {loading ? <CircularProgress size={24} sx={{ color: 'hsl(var(--sidebar-primary))' }} /> : "Load More"}
          </Button>
        </Box>
      )}

      <DeleteConfirmationModal
        open={deleteTarget !== null}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        fileName={deleteTarget?.filename || ""}
        loading={deleteLoading}
      />
      <BulkDeleteConfirmationModal
        open={bulkDeleteKeys !== null}
        count={bulkDeleteKeys?.length ?? 0}
        onCancel={cancelBulkDelete}
        onConfirm={confirmBulkDelete}
        loading={bulkBusy}
      />
      <RenameFileModal
        open={selectedFileForRename !== null}
        onCancel={cancelRename}
        onConfirm={confirmRename}
        currentDisplayName={selectedFileForRename?.title || selectedFileForRename?.filename || ""}
        loading={renameLoading}
      />

      {/* Document Chat Modal */}
      {selectedItemForChat && selectedItemForChat.jobId && (
        <DocumentChatModal
          open={chatModalOpen}
          onClose={handleCloseChatModal}
          sourceJobId={selectedItemForChat.jobId}
          documentTitle={selectedItemForChat.filename}
          expiresAt={selectedItemForChat.embeddingsExpiresAt}
        />
      )}
    </Box>
  );
};

export default GenericList;
